import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { ArrowRightLeft, Loader2, Search, User } from "lucide-react";
import api from "../../../services/api";

export default function ConsortiumTransferModal({ consortium, open, onClose, onTransferred }) {
  var clientsState = useState([]);
  var clients = clientsState[0];
  var setClients = clientsState[1];
  var searchState = useState("");
  var search = searchState[0];
  var setSearch = searchState[1];
  var selectedState = useState(null);
  var selected = selectedState[0];
  var setSelected = selectedState[1];
  var loadingState = useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];
  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];
  var successState = useState(null);
  var success = successState[0];
  var setSuccess = successState[1];

  useEffect(function() {
    if (!open) return;
    setLoading(true);
    api.get("/clients?limit=200").then(function(r) {
      var all = r.data.clients || [];
      setClients(all.filter(function(c) { return c.id !== consortium?.client_id; }));
    }).catch(function() {}).finally(function() { setLoading(false); });
  }, [open, consortium?.client_id]);

  var filtered = clients.filter(function(c) {
    if (!search) return true;
    var q = search.toLowerCase();
    return (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q) || (c.cpf || "").includes(q);
  });

  async function handleTransfer() {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      var res = await api.post("/consortiums/" + consortium.id + "/transfer", {
        target_client_id: selected.id,
      });
      setSuccess(res.data.message);
      setTimeout(function() {
        onTransferred();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao transferir");
    } finally {
      setSaving(false);
    }
  }

  function handleClose() {
    setSearch("");
    setSelected(null);
    setError(null);
    setSuccess(null);
    onClose();
  }

  if (!consortium) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A2F3D]">
            <ArrowRightLeft className="w-5 h-5 text-amber-600" />
            Transferir Cota de Consorcio
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2" data-testid="consortium-transfer-modal">
          {/* Current consortium info */}
          <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
            <p className="text-xs text-gray-500 mb-1">Consorcio a transferir:</p>
            <p className="text-sm font-semibold text-[#0A2F3D]">Grupo {consortium.grupo || "-"} | Cota {consortium.cota || "-"}</p>
            <p className="text-xs text-gray-500">{consortium.administrator || "-"} | R$ {Number(consortium.value || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
          </div>

          {success ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
              <p className="text-sm font-medium text-emerald-700">{success}</p>
            </div>
          ) : (
            <>
              {/* Search client */}
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Selecione o cliente destino</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="text" value={search} onChange={function(e) { setSearch(e.target.value); }}
                    placeholder="Buscar por nome, email ou CPF..."
                    className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                    data-testid="transfer-search-input" />
                </div>
              </div>

              {/* Client list */}
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl">
                {loading ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                ) : filtered.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-6">Nenhum cliente encontrado</p>
                ) : (
                  filtered.slice(0, 50).map(function(c) {
                    var isSelected = selected && selected.id === c.id;
                    return (
                      <div key={c.id}
                        onClick={function() { setSelected(c); }}
                        className={"flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-gray-50 last:border-0 transition-colors " + (isSelected ? "bg-amber-50 border-amber-200" : "hover:bg-gray-50")}
                        data-testid={"transfer-client-" + c.id}>
                        <div className={"w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 " + (isSelected ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-500")}>
                          <User className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#0A2F3D] truncate">{c.name}</p>
                          <p className="text-[10px] text-gray-500 truncate">{c.email} {c.cpf ? " | " + c.cpf : ""}</p>
                        </div>
                        {isSelected && <span className="text-xs font-medium text-amber-600">Selecionado</span>}
                      </div>
                    );
                  })
                )}
              </div>

              {error && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

              {/* Transfer button */}
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button onClick={handleClose}
                  className="px-4 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  data-testid="transfer-cancel">
                  Cancelar
                </button>
                <button onClick={handleTransfer} disabled={saving || !selected}
                  className="px-4 py-2 text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="transfer-confirm">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRightLeft className="w-3.5 h-3.5" />}
                  Transferir Cota
                </button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
